/**
 * Organization Capabilities Management Component
 * Simplified flat-table admin interface with inline toggle switches.
 * Uses optimistic state updates — no full refetch, no scroll reset.
 */

import React, { useState, useEffect } from 'react';
import { Settings, X, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { useAppTranslation } from '@/contexts/TranslationContext';

interface Capability {
  id: number;
  organization_id: number;
  capability_name: string;
  enabled: boolean;
  config: Record<string, any>;
  created_at: string;
}

interface AvailableCapability {
  label: string;
  description: string;
  category: string;
  always_enabled?: boolean;
}

type DisplayBehavior = 'hide' | 'disable' | 'show-upgrade';

/** A page whose visibility a feature capability controls (page_capabilities.parent_capability). */
interface ControlledPage {
  page_key: string;
  page_name: string;
  page_name_es: string | null;
  effective_enabled: boolean;
}

// Effects a capability has that are NOT page_capabilities rows, so the page
// list cannot show them. V2Sidebar builds the whole "Training & Workforce"
// section (Learning, Training) only while `lms` is on — turning lms off for
// Amigos on 2026-10-15 removed it for every user with no warning (TRUS-662).
// `unlessEnabled`: the effect doesn't apply while that capability is on. Since #860
// the sidebar shows Training for lms OR staff_training, so an org with staff_training
// keeps it when lms goes off (TRUS-766).
const EXTRA_EFFECTS: Record<string, { key: string; fallback: string; unlessEnabled?: string }[]> = {
  lms: [
    {
      key: 'admin.cap_effect_training_workforce_nav',
      fallback: 'Training & Workforce sidebar section (Learning, Training)',
      unlessEnabled: 'staff_training',
    },
  ],
};

interface CapabilitiesManagementProps {
  organizationId: number;
  organizationName: string;
  onClose?: () => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  core: 'Core Features',
  engagement: 'Engagement & Communication',
  care_coordination: 'Care Coordination',
  compliance: 'Compliance & Verification',
  automation: 'Automation & Workflows',
  financial: 'Financial & Payments',
  insights: 'Analytics & Reporting',
  collaboration: 'Collaboration & Integration',
  advanced: 'Advanced Features',
  accessibility: 'Accessibility',
  employer: 'Employer Navigation Platform',
  workforce: 'Workforce & Training',
  marketing_sales: 'Marketing & Sales Platform',
  platform: 'Platform Access',
  cbo_enhanced: 'CBO Enhanced Capabilities',
};

const CATEGORY_ORDER = [
  'core', 'engagement', 'care_coordination', 'compliance', 'financial',
  'automation', 'insights', 'collaboration', 'advanced', 'workforce', 'employer',
  'marketing_sales', 'platform', 'accessibility', 'cbo_enhanced',
];

export function CapabilitiesManagement({
  organizationId,
  organizationName,
  onClose,
}: CapabilitiesManagementProps) {
  const { t, language } = useAppTranslation();
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [availableCapabilities, setAvailableCapabilities] = useState<Record<string, AvailableCapability>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingItems, setSavingItems] = useState<Set<string>>(new Set());
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [pagesByCapability, setPagesByCapability] = useState<Record<string, ControlledPage[]>>({});

  useEffect(() => {
    Promise.all([loadCapabilities(), loadAvailableCapabilities(), loadControlledPages()]).finally(() => setLoading(false));
  }, [organizationId]);

  // Which pages each capability switches on and off, so an admin can see the
  // consequence of a toggle before making it (TRUS-662). Best-effort: the
  // screen still works without it, it just can't list the pages.
  const loadControlledPages = async () => {
    try {
      const response = await apiClient.get(`/api/organizations/${organizationId}/page-capabilities`, {
        headers: { 'X-CBO-ID': organizationId.toString() },
      });
      const grouped: Record<string, ControlledPage[]> = {};
      for (const p of (response.data || []) as Array<ControlledPage & { parent_capability: string | null }>) {
        if (!p.parent_capability) continue;
        (grouped[p.parent_capability] ||= []).push(p);
      }
      setPagesByCapability(grouped);
    } catch (err: any) {
      console.warn('Failed to load pages controlled by capabilities:', err);
    }
  };

  const pageLabel = (p: ControlledPage) => (language === 'es' && p.page_name_es) || p.page_name;

  /** The non-page effects that apply to this org right now. */
  const extraEffectsOf = (capName: string) =>
    (EXTRA_EFFECTS[capName] || []).filter((e) => !e.unlessEnabled || !isEnabled(e.unlessEnabled));

  /** Everything a capability controls, as display names. */
  const effectsOf = (capName: string): string[] => [
    ...(pagesByCapability[capName] || []).map(pageLabel),
    ...extraEffectsOf(capName).map((e) => t(e.key, e.fallback)),
  ];

  const loadCapabilities = async () => {
    try {
      const response = await apiClient.get(`/api/organizations/${organizationId}/capabilities`, {
        headers: { 'X-CBO-ID': organizationId.toString() },
      });
      setCapabilities(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load capabilities');
    }
  };

  const loadAvailableCapabilities = async () => {
    try {
      const response = await apiClient.get('/api/capabilities/available');
      setAvailableCapabilities(response.data);
    } catch (err: any) {
      console.error('Failed to load available capabilities:', err);
    }
  };

  const isEnabled = (name: string): boolean => {
    const cap = capabilities.find((c) => c.capability_name === name);
    if (name === 'member_management' && !cap) return true;
    return cap?.enabled || false;
  };

  const getDisplayBehavior = (name: string): DisplayBehavior => {
    const cap = capabilities.find((c) => c.capability_name === name);
    return (cap?.config?.display_behavior as DisplayBehavior) || 'hide';
  };

  // Optimistic toggle — update local state immediately, then sync with server
  const handleToggle = async (capName: string, currentEnabled: boolean) => {
    const newEnabled = !currentEnabled;

    // Turning a capability off hides pages for everyone in the org. Say which,
    // and ask, before doing it.
    const effects = effectsOf(capName);
    if (!newEnabled && effects.length > 0) {
      const label = availableCapabilities[capName]?.label || capName;
      const ok = window.confirm(
        t(
          'admin.cap_disable_confirm',
          'Turning off "{{label}}" hides these for everyone in {{org}}:\n\n{{items}}\n\nTurn it off?',
          { label, org: organizationName, items: effects.map((e) => `• ${e}`).join('\n') },
        ),
      );
      if (!ok) return;
    }

    // Optimistic update
    setCapabilities((prev) => {
      const existing = prev.find((c) => c.capability_name === capName);
      if (existing) {
        return prev.map((c) =>
          c.capability_name === capName ? { ...c, enabled: newEnabled } : c
        );
      }
      // Add new entry optimistically
      return [...prev, {
        id: 0,
        organization_id: organizationId,
        capability_name: capName,
        enabled: newEnabled,
        config: {},
        created_at: new Date().toISOString(),
      }];
    });

    setSavingItems((prev) => new Set(prev).add(capName));
    try {
      // Always PUT — the backend endpoint upserts (creates the row if the org
      // was never seeded this capability). Previously this branched to POST for
      // missing rows and hardcoded enabled:true, so toggling OFF a never-seeded
      // capability silently created it as enabled; and PUT used to 404 for
      // synthesized always-enabled capabilities, snapping the switch back.
      await apiClient.put(
        `/api/organizations/${organizationId}/capabilities/${capName}`,
        { enabled: newEnabled },
        { headers: { 'X-CBO-ID': organizationId.toString() } }
      );
      // The pages' visible/hidden state just changed — refresh the badges.
      loadControlledPages();
    } catch (err: any) {
      // Revert on failure: restore prior enabled state, and drop any entry that
      // was only added optimistically (id===0) so a failed first-toggle doesn't
      // leave a phantom row in the list.
      setCapabilities((prev) =>
        prev
          .map((c) => (c.capability_name === capName ? { ...c, enabled: currentEnabled } : c))
          .filter((c) => !(c.capability_name === capName && c.id === 0))
      );
      alert(`Failed to update: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSavingItems((prev) => {
        const next = new Set(prev);
        next.delete(capName);
        return next;
      });
    }
  };

  const handleBehaviorChange = async (capName: string, behavior: DisplayBehavior) => {
    setSavingItems((prev) => new Set(prev).add(capName));
    try {
      const existing = capabilities.find((c) => c.capability_name === capName);
      if (existing) {
        await apiClient.put(
          `/api/organizations/${organizationId}/capabilities/${capName}`,
          { enabled: existing.enabled, config: { ...existing.config, display_behavior: behavior } },
          { headers: { 'X-CBO-ID': organizationId.toString() } }
        );
        setCapabilities((prev) =>
          prev.map((c) =>
            c.capability_name === capName
              ? { ...c, config: { ...c.config, display_behavior: behavior } }
              : c
          )
        );
      } else {
        await apiClient.post(
          `/api/organizations/${organizationId}/capabilities`,
          { capability_name: capName, enabled: false, config: { display_behavior: behavior } },
          { headers: { 'X-CBO-ID': organizationId.toString() } }
        );
        setCapabilities((prev) => [
          ...prev,
          {
            id: 0,
            organization_id: organizationId,
            capability_name: capName,
            enabled: false,
            config: { display_behavior: behavior },
            created_at: new Date().toISOString(),
          },
        ]);
      }
    } catch (err: any) {
      alert(`Failed to update: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSavingItems((prev) => {
        const next = new Set(prev);
        next.delete(capName);
        return next;
      });
    }
  };

  const toggleCategory = (cat: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  // Group and sort capabilities
  const grouped = Object.entries(availableCapabilities).reduce(
    (acc, [name, details]) => {
      const cat = details.category || 'other';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push({ name, ...details });
      return acc;
    },
    {} as Record<string, Array<{ name: string } & AvailableCapability>>
  );

  const sortedCategories = CATEGORY_ORDER.filter((c) => grouped[c]);
  // Add any categories not in the order list
  Object.keys(grouped).forEach((c) => {
    if (!sortedCategories.includes(c)) sortedCategories.push(c);
  });

  // Filter by search
  const matchesSearch = (cap: { name: string; label: string; description: string }) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return cap.label.toLowerCase().includes(q) || cap.description.toLowerCase().includes(q) || cap.name.toLowerCase().includes(q);
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-3" />
        Loading capabilities...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-3" />
        <p className="text-red-600 mb-3">{error}</p>
        <button onClick={() => { setError(null); setLoading(true); loadCapabilities().finally(() => setLoading(false)); }} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
          Retry
        </button>
      </div>
    );
  }

  const enabledCount = Object.keys(availableCapabilities).filter((n) => isEnabled(n)).length;
  const totalCount = Object.keys(availableCapabilities).length;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Settings className="h-6 w-6 text-indigo-600" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Feature Capabilities
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {organizationName} &middot; {enabledCount} of {totalCount} enabled
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Search capabilities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800"
          />
          {onClose && (
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      {/* Category sections */}
      {sortedCategories.map((category) => {
        const caps = grouped[category].filter(matchesSearch);
        if (caps.length === 0) return null;
        const isCollapsed = collapsedCategories.has(category);
        const catEnabled = caps.filter((c) => isEnabled(c.name)).length;

        return (
          <div key={category} className="mb-4">
            {/* Category header — click to collapse */}
            <button
              onClick={() => toggleCategory(category)}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors"
            >
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {CATEGORY_LABELS[category] || category}
                </h3>
                <span className="text-xs text-gray-400">
                  {catEnabled}/{caps.length} enabled
                </span>
              </div>
              {isCollapsed ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronUp className="h-4 w-4 text-gray-400" />}
            </button>

            {/* Capability rows */}
            {!isCollapsed && (
              <div className="mt-1 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden divide-y divide-gray-100 dark:divide-gray-800">
                {caps.map((cap) => {
                  const enabled = isEnabled(cap.name);
                  const isSaving = savingItems.has(cap.name);
                  const isAlways = cap.always_enabled;
                  const behavior = getDisplayBehavior(cap.name);

                  return (
                    <div
                      key={cap.name}
                      className={`flex items-center gap-4 px-4 py-3 transition-colors ${
                        enabled ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/50 dark:bg-gray-900/50'
                      }`}
                    >
                      {/* Toggle switch */}
                      <div className="flex-shrink-0">
                        {isAlways ? (
                          <span className="inline-block w-11 h-6 bg-green-500 rounded-full relative cursor-not-allowed opacity-60">
                            <span className="absolute right-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow" />
                          </span>
                        ) : (
                          <button
                            onClick={() => handleToggle(cap.name, enabled)}
                            disabled={isSaving}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                              enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                            } ${isSaving ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                            aria-label={`Toggle ${cap.label}`}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                enabled ? 'translate-x-5' : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                        )}
                      </div>

                      {/* Label & description */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${enabled ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
                            {cap.label}
                          </span>
                          {isAlways && (
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                              Required
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-normal break-words">
                          {cap.description}
                        </p>
                        {(() => {
                          const pages = pagesByCapability[cap.name] || [];
                          const extras = extraEffectsOf(cap.name);
                          if (pages.length === 0 && extras.length === 0) return null;
                          return (
                            <div className="mt-1.5 flex flex-wrap items-center gap-1">
                              <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                                {t('admin.cap_controls', 'Controls:')}
                              </span>
                              {extras.map((e) => (
                                <span key={e.key} className={`text-[11px] px-1.5 py-0.5 rounded ${enabled ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' : 'bg-gray-100 text-gray-500 line-through dark:bg-gray-800'}`}>
                                  {t(e.key, e.fallback)}
                                </span>
                              ))}
                              {pages.map((p) => (
                                <span
                                  key={p.page_key}
                                  title={p.effective_enabled ? t('admin.cap_page_visible', 'Visible to this organization') : t('admin.cap_page_hidden', 'Hidden from this organization')}
                                  className={`text-[11px] px-1.5 py-0.5 rounded ${p.effective_enabled ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' : 'bg-gray-100 text-gray-500 line-through dark:bg-gray-800'}`}
                                >
                                  {pageLabel(p)}
                                </span>
                              ))}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Display behavior (only when disabled) */}
                      <div className="flex-shrink-0 w-40">
                        {!enabled && !isAlways ? (
                          <select
                            value={behavior}
                            onChange={(e) => handleBehaviorChange(cap.name, e.target.value as DisplayBehavior)}
                            disabled={isSaving}
                            className="w-full text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="hide">Hide completely</option>
                            <option value="disable">Show locked</option>
                            <option value="show-upgrade">Show upgrade</option>
                          </select>
                        ) : (
                          <span className="text-xs text-gray-300 dark:text-gray-600">&nbsp;</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default CapabilitiesManagement;
